<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { eqsApi } from '@/api/eqs';
import type { SpecialCharacteristic, SpecialCharacteristicsResponse } from '@/types/domain';
import { errorMessage } from '@/utils/format';

/**
 * 特殊特性贯通核对（PFMEA 与控制计划两环对不对得上）。
 *
 * 0910 Tony:「pfmea 界面不直观，很多页面都凑一块了」。这块原来是页面最上面
 * 一张 700px 高的大卡片、永远全展开——而真正干活的结构树（SN26003 那份 4984 条目）
 * 被压在它、整簿导入、记录列表、正文头**四块后面**，每次都要滚过去。
 *
 * 它是一个**检查**，不是每天要做的事，所以收成一条横幅：
 *   - 有对不上的 → 标红、**默认展开**（有问题就该看见，行为和原来一样）
 *   - 全对上   → 一行灰字带上数字，默认收起
 * 结论写在 summary 上，收起状态也知道通没通——收起不等于藏起来。
 */
const props = defineProps<{
  projectId: number | string;
  /**
   * 在自己的子标签页里显示（PFMEA 的「核对」页）：直接摊开，不套横幅。
   * 已经点进这一页了，再让人点一次「展开」是多余的一步。
   * 控制计划页仍走横幅——那边它是配角。
   *
   * 用 `v-show` 藏掉 summary 而不是 `v-if`：`<details>` 没有 `<summary>` 时
   * 浏览器会自己画一个「Details」出来，比留着更难看。
   */
  embedded?: boolean;
}>();
/** 把缺口条数报给外面：挪进子标签之后，标签上得挂个数，否则等于把问题藏起来 */
const emit = defineEmits<{ loaded: [gaps: number] }>();

const data = ref<SpecialCharacteristicsResponse | null>(null);
const loading = ref(false);
const error = ref('');

async function load() {
  loading.value = true;
  error.value = '';
  try {
    data.value = await eqsApi.specialCharacteristics(props.projectId);
    emit('loaded', data.value?.gaps?.length ?? 0);
  } catch (caught) {
    data.value = null;
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
}
watch(() => props.projectId, load, { immediate: true });

const totals = computed(() => data.value?.totals);
const gaps = computed(() => data.value?.gaps ?? []);
/** 整体判断：先说清是「真没做控制」还是「两边名称口径不一致」 */
const verdict = computed(() => data.value?.verdict ?? null);

/** 收起时这一行就是全部信息，所以结论要写进去 */
const headline = computed(() => {
  if (error.value) return '读取失败';
  if (!data.value) return loading.value ? '读取中…' : '';
  if (gaps.value.length) return `${gaps.value.length} 条特殊特性没对上控制计划`;
  const counts = totals.value;
  return `${counts?.pfmea ?? 0} 条 PFMEA 声明全部对上控制计划的 ${counts?.controlPlan ?? 0} 个标记行`;
});

/**
 * 两张明细表也按「同一件事」合并。
 *
 * 14 个零件各一份 PFMEA、各一份控制计划，同样 2 条特性就摊成 28 + 28 行，
 * 滚三屏看到的是同两句话（试用反馈 8/13）。合并后每组一行，
 * 点开才展开每份文档的具体写法——要核对时仍然查得到。
 */
interface GroupedRow {
  key: string;
  symbol: string;
  description: string;
  specification: string | null;
  stepNumber: string | null;
  count: number;
  details: Array<{ documentTitle: string | null; controlMethod: string | null }>;
  /** 控制方法有几种不同写法；同一个控制写成 N 种是要治的 */
  methodVariants: number;
}

function group(items: SpecialCharacteristic[], byStep: boolean): GroupedRow[] {
  const map = new Map<string, GroupedRow>();
  for (const item of items) {
    const key = `${item.symbol}|${byStep ? item.stepNumber ?? '' : ''}|${item.description.trim()}`;
    const existing = map.get(key);
    if (existing) {
      existing.count += 1;
      existing.details.push({ documentTitle: item.documentTitle, controlMethod: item.controlMethod });
      continue;
    }
    map.set(key, {
      key,
      symbol: item.symbol,
      description: item.description,
      specification: item.specification,
      stepNumber: item.stepNumber,
      count: 1,
      details: [{ documentTitle: item.documentTitle, controlMethod: item.controlMethod }],
      methodVariants: 0,
    });
  }
  for (const row of map.values()) {
    row.methodVariants = new Set(
      row.details.map((detail) => (detail.controlMethod ?? '').trim()).filter(Boolean),
    ).size;
  }
  return [...map.values()];
}

const pfmeaGroups = computed(() => group(data.value?.pfmea ?? [], false));
const controlGroups = computed(() => group(data.value?.controlPlan ?? [], true));

const expanded = ref<Set<string>>(new Set());
function toggle(key: string): void {
  const next = new Set(expanded.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  expanded.value = next;
}
</script>

<template>
  <details
    class="sc-panel"
    :class="{ 'has-gaps': gaps.length > 0 && !embedded, 'is-embedded': embedded }"
    :open="embedded || gaps.length > 0"
  >
    <summary v-show="!embedded">
      <!-- 显式画箭头：flex 布局下 summary 自带的那个三角不出来，看不出这行能点 -->
      <span class="sc-caret" aria-hidden="true">▸</span>
      <span class="sc-kicker">SPECIAL CHARACTERISTICS</span>
      <b>特殊特性贯通核对</b>
      <em v-if="headline" :class="{ 'sc-bad': gaps.length > 0 }">{{ headline }}</em>
    </summary>

    <div class="sc-body">
      <p class="sc-intro">
        <button class="inline-button sc-refresh" type="button" :disabled="loading" @click.prevent="load">
          {{ loading ? '读取中…' : '刷新' }}
        </button>
      IATF 16949 8.3.3.3 要求特殊特性形成完整链条：图纸/客户要求 → DFMEA → PFMEA → 控制计划 → 作业指导书 → 检具 → SPC。
      本系统覆盖 <b>PFMEA</b> 与 <b>控制计划</b> 两环，下方核对这两环是否对得上。
    </p>

    <p v-if="error" class="sc-error">{{ error }}</p>

    <template v-else-if="data">
      <div class="sc-totals">
        <span><b>{{ totals?.pfmea }}</b>PFMEA 声明</span>
        <span><b>{{ totals?.controlPlan }}</b>控制计划标记行</span>
        <span><b>{{ totals?.cc }}</b>CC 关键特性</span>
        <span><b>{{ totals?.sc }}</b>SC 重要特性</span>
      </div>

<!--
        缺口按「特性」列，不按「行」列：同样 2 条特性在 14 份 PFMEA 里各声明一遍，
        原来会报 28 条，一屏红字，人只会划过去（试用反馈 8/13）。
      -->
      <div v-if="gaps.length" class="sc-gaps">
        <b>{{ gaps.length }} 条特殊特性没有对上控制计划</b>
        <p v-if="verdict" class="sc-verdict">{{ verdict.summary }}</p>
        <ul>
          <li v-for="(gap, index) in gaps" :key="index">
            <i v-if="gap.symbol" class="sc-symbol">{{ gap.symbol }}</i>
            <b>{{ gap.description }}</b>
            <em v-if="gap.affectedDocuments > 1" class="sc-affected">
              涉及 {{ gap.affectedDocuments }} 份 PFMEA
            </em>
            <span class="sc-reason">{{ gap.reason }}</span>
          </li>
        </ul>
      </div>
      <p v-else class="sc-ok">PFMEA 声明的特殊特性均能在控制计划中找到对应控制行。</p>

      <div class="sc-tables">
        <div>
          <h4>
            PFMEA 声明的特殊特性（{{ pfmeaGroups.length }} 条）
            <small v-if="data.pfmea.length !== pfmeaGroups.length">
              摊开是 {{ data.pfmea.length }} 处声明
            </small>
          </h4>
          <table v-if="pfmeaGroups.length">
            <thead><tr><th>符号</th><th>特性</th><th>规范 / 公差</th></tr></thead>
            <tbody>
              <template v-for="row in pfmeaGroups" :key="row.key">
                <tr>
                  <td><i class="sc-symbol">{{ row.symbol || '—' }}</i></td>
                  <td>
                    {{ row.description }}
                    <button v-if="row.count > 1" type="button" class="sc-more" @click="toggle(row.key)">
                      {{ row.count }} 份 PFMEA{{ expanded.has(row.key) ? ' ▴' : ' ▾' }}
                    </button>
                  </td>
                  <td>{{ row.specification || '—' }}</td>
                </tr>
                <tr v-if="expanded.has(row.key)" class="sc-detail">
                  <td colspan="3">
                    <span v-for="(detail, index) in row.details" :key="index">{{ detail.documentTitle || '未命名' }}</span>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <p v-else class="sc-empty">本项目 PFMEA 中尚未标注特殊特性。</p>
        </div>

        <div>
          <h4>
            控制计划中的控制行（{{ controlGroups.length }} 组）
            <small v-if="data.controlPlan.length !== controlGroups.length">
              摊开是 {{ data.controlPlan.length }} 行
            </small>
          </h4>
          <table v-if="controlGroups.length">
            <thead><tr><th>符号</th><th>工序</th><th>特性</th><th>控制方法</th></tr></thead>
            <tbody>
              <template v-for="row in controlGroups" :key="row.key">
                <tr>
                  <td><i class="sc-symbol">{{ row.symbol || '—' }}</i></td>
                  <td>{{ row.stepNumber || '—' }}</td>
                  <td>
                    {{ row.description }}
                    <button v-if="row.count > 1" type="button" class="sc-more" @click="toggle(row.key)">
                      {{ row.count }} 行{{ expanded.has(row.key) ? ' ▴' : ' ▾' }}
                    </button>
                  </td>
                  <td>
                    {{ row.details[0]?.controlMethod || '—' }}
                    <!-- 同一个控制被写成 N 种说法：审核时对不上，值得当场看见 -->
                    <em v-if="row.methodVariants > 1" class="sc-variants">
                      另有 {{ row.methodVariants - 1 }} 种写法
                    </em>
                  </td>
                </tr>
                <tr v-if="expanded.has(row.key)" class="sc-detail">
                  <td colspan="4">
                    <span v-for="(detail, index) in row.details" :key="index">
                      {{ detail.documentTitle || '未命名' }}：{{ detail.controlMethod || '—' }}
                    </span>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <p v-else class="sc-empty">本项目控制计划中没有带 CC/SC 标记的行。</p>
        </div>
      </div>
    </template>
    </div>
  </details>
</template>

<style scoped>
.sc-panel > summary {
  display: flex; flex-wrap: wrap; align-items: baseline; gap: 8px;
  padding: 9px 12px; cursor: pointer; list-style: none;
}
.sc-panel > summary::-webkit-details-marker { display: none; }
.sc-caret { color: var(--muted); font-size: 11px; transition: transform .12s; }
.sc-panel[open] > summary .sc-caret { transform: rotate(90deg); display: inline-block; }
.sc-panel > summary:hover { background: #F4F6F8; }
.sc-kicker { color: var(--blue); font: 800 11px var(--display); letter-spacing: .1em; }
.sc-panel > summary b { font-size: 13px; color: var(--ink); }
.sc-panel > summary em { font-style: normal; font-size: 12px; color: var(--muted); }
.sc-panel > summary em.sc-bad { color: var(--risk); font-weight: 600; }
.sc-panel.has-gaps > summary { box-shadow: inset 3px 0 var(--risk); }
.sc-body { padding: 0 12px 12px; }
/* 子标签页里它是主角：不留横幅那圈边框和缩进 */
.sc-panel.is-embedded { border: 0; background: transparent; }
.sc-panel.is-embedded .sc-body { padding: 0; }
.sc-refresh { float: right; margin-left: 8px; }
.sc-panel { display: grid; gap: 10px; padding: 14px 16px; margin-bottom: 14px; border: 1px solid var(--line); background: var(--white); }
.sc-panel > header { display: flex; align-items: flex-end; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.sc-panel header span { color: var(--muted); font: 700 10px var(--display); letter-spacing: .08em; }
.sc-panel header h3 { margin: 2px 0 0; font-size: 15px; }
.sc-intro { margin: 0; color: var(--muted); font-size: 12px; line-height: 1.8; }
.sc-error { margin: 0; padding: 8px 10px; border-left: 3px solid #c0392b; background: #fdecea; color: #922; font-size: 12px; }
.sc-totals { display: flex; flex-wrap: wrap; gap: 14px; padding: 8px 10px; background: var(--paper); font-size: 12px; color: var(--muted); }
.sc-totals b { margin-right: 4px; color: var(--ink); font-size: 14px; }
.sc-gaps { padding: 9px 11px; border-left: 3px solid #c0392b; background: #fdecea; font-size: 12px; }
.sc-gaps > b { color: #922; }
.sc-gaps ul { margin: 6px 0 0; padding-left: 16px; line-height: 1.7; color: #7a2018; }
.sc-gaps li { margin-bottom: 6px; }
.sc-verdict { margin: 5px 0 0; color: #7a2018; line-height: 1.8; }
.sc-tables h4 small { margin-left: 6px; color: var(--muted); font-size: 11px; font-weight: 400; }
.sc-more { margin-left: 6px; padding: 0 7px; border: 1px solid var(--line); border-radius: 999px; background: var(--white); color: var(--blue); font-size: 11px; font-weight: 700; cursor: pointer; white-space: nowrap; }
.sc-more:hover { border-color: var(--blue); }
.sc-variants { display: block; margin-top: 2px; color: #8a5a00; font-style: normal; font-size: 11px; }
.sc-detail td { background: #f7f9fc; }
.sc-detail span { display: block; padding: 2px 0; color: var(--muted); font-size: 11px; line-height: 1.6; }
.sc-affected { display: inline-block; margin-left: 6px; padding: 0 6px; border-radius: 999px; background: rgba(146, 34, 34, .12); font-style: normal; font-size: 11px; }
.sc-reason { display: block; margin-top: 2px; color: #96524a; }
.sc-ok { margin: 0; padding: 8px 10px; border-left: 3px solid #2b6b32; background: #eef7ec; color: #2b6b32; font-size: 12px; }
.sc-tables { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 12px; }
.sc-tables h4 { margin: 0 0 6px; font-size: 12px; }
.sc-tables table { width: 100%; border-collapse: collapse; font-size: 12px; }
.sc-tables th { padding: 4px 6px; border-bottom: 1px solid var(--line); color: var(--muted); font-weight: 700; text-align: left; }
.sc-tables td { padding: 4px 6px; border-bottom: 1px solid var(--paper); vertical-align: top; }
.sc-symbol { display: inline-block; padding: 0 4px; border-radius: 3px; background: #fdecea; color: #922; font-size: 12px; font-style: normal; font-weight: 800; }
.sc-empty { margin: 0; color: var(--muted); font-size: 12px; }
</style>
